import {areSetsEqual, noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER, NEAR_BLACK} from 'ui-library/Colors';
import {RequiredIdsProps, SelectTreeProps} from 'ui-library/select-tree/SelectTree';
import {TreeItem, TreeItemStateMap} from 'ui-library/select-tree/SelectTreeConstants';
import SelectTreeListItem from 'ui-library/select-tree/SelectTreeListItem';
import SelectTreeNode, {
  SelectTreeAutoLoadMoreNode,
  SelectTreeLoadingNode,
  SelectTreeLoadMoreNode,
  SelectTreeNotFoundNode,
} from 'ui-library/select-tree/SelectTreeNode';
import * as SelectTreeUtil from 'ui-library/select-tree/SelectTreeUtil';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

export type ControlledSelectTreeProps = RequiredIdsProps & {
  autoLoadMore?: boolean;
  /** When enabled, includes an option to deselect all items */
  allowNone: boolean;
  /** When enabled and `multiple` is true, includes an option to select all items. */
  includeSelectAllItem?: boolean;
  canLoadMore: () => boolean;
  className?: string;
  clickedItems: Array<string>;
  disabled?: boolean;
  handleClickedItemsChange: (newClickedItem: string) => void;
  hasQuery: boolean;
  isLoadedItemExpandable?: ((item: TreeItem) => boolean) | null;
  isLoadingRootItems: boolean;
  itemStateMap: TreeItemStateMap;
  itemTypeName: string;
  loadMoreFromIds: (ids: Array<string>) => void;
  moreRootItems: boolean;
  multiple: boolean;
  onChangeSelectedIds: (selectedIds: Array<string>) => void;
  onLoadMoreRootItems?: () => void;
  rootItems: Array<TreeItem>;
  selectAllText?: string | null;
  selectedIds: Array<string>;
  siblingSortFn?: SelectTreeProps['siblingSortFn'];
  submitHandler?: () => void;
  toggleExpanded: (itemId: string) => void;
  headers?: SelectTreeProps['headers'];
  columnWidths?: SelectTreeProps['columnWidths'];
  isItemSelectable?: (item: TreeItem) => boolean;
  itemClassName?: string;
  selectedItemClassName?: string;
  onHoverItem?: (id: string, isHovering: boolean) => void;
  rootRef?: React.RefObject<HTMLUListElement>;
};

/**
 * A controlled version of SelectTree that handles only rendering and selection logic. This was refactored to
 * create a different flavor of SelectTree and is not intended for stand-alone use as part of ui-library.
 */
export default function ControlledSelectTree(props: ControlledSelectTreeProps): JSX.Element {
  const {isItemSelectable: propsIsItemSelectable} = props;

  const isItemSelectable = useCallback(
    (item: TreeItem): boolean => (propsIsItemSelectable ? propsIsItemSelectable(item) : true),
    [propsIsItemSelectable]
  );

  const selectableItemIds = useMemo(
    () =>
      new Set(
        [
          props.rootItems,
          ...Object.values(props.itemStateMap).map((itemState): Array<TreeItem> => [
            ...itemState.baseLeafItems,
            ...itemState.childItems,
            ...itemState.leafItems,
          ]),
        ]
          .flat()
          .filter((item) => isItemSelectable(item))
          .map(({id}) => id)
      ),
    [props.rootItems, props.itemStateMap, isItemSelectable]
  );

  const areAllSelectableItemsSelected = useMemo(() => {
    function findAllSelectedIds(): Set<string> {
      if (!props.multiple) {
        return new Set(props.selectedIds);
      }

      const queue = [...props.selectedIds];
      const allSelectedIds: Array<string> = [];
      while (queue.length > 0) {
        const selectedId = queue.pop()!;
        allSelectedIds.push(selectedId);
        const itemState = props.itemStateMap[selectedId];
        if (itemState == null) {
          continue;
        }
        const children = [...itemState.childItems, ...itemState.baseLeafItems, ...itemState.leafItems]
          .filter((item) => isItemSelectable(item))
          .map(({id}) => id);
        queue.push(...children);
      }
      return new Set(allSelectedIds);
    }

    return areSetsEqual(selectableItemIds, findAllSelectedIds());
  }, [selectableItemIds, props.selectedIds, props.multiple, props.itemStateMap, isItemSelectable]);

  /**
   * If all selectable items are selected, deselects them, except for any requiredRootId items;
   * otherwise (if none or some are selected), selects all selectable items.
   */
  const toggleSelectAll = (): void => {
    const minimumSelection = props.requiredRootIds || [];
    props.onChangeSelectedIds(areAllSelectableItemsSelected ? minimumSelection : [...selectableItemIds]);
  };

  function isNoneSelected(): boolean {
    return props.selectedIds.length === 0;
  }

  const selectNone = (): void => {
    props.onChangeSelectedIds([]);
  };

  function handleEnterPress(e: ElementKeyboardEvent): void {
    if (keyboard.isEnterKey(e)) {
      props.submitHandler!();
    }
  }

  function getNextSelectedIds(clickedItemId: string, baseNextSelectedIds: Array<string>): Array<string> {
    const allDisplayedItems = SelectTreeUtil.getAllDisplayedIds(
      props.itemStateMap,
      props.rootItems,
      props.siblingSortFn
    );
    let shiftSelectedIds = SelectTreeUtil.findShiftSelectedIds(
      baseNextSelectedIds,
      allDisplayedItems,
      clickedItemId,
      props.selectedIds,
      props.clickedItems
    );
    shiftSelectedIds = shiftSelectedIds.filter((itemId) => selectableItemIds.has(itemId));
    return shiftSelectedIds;
  }

  let selectNoneItem;
  let selectAllItem;
  if (props.allowNone) {
    const _isNoneSelected = isNoneSelected();
    selectNoneItem = (
      <SelectTreeListItem
        disabled={props.disabled}
        icon="none-option"
        isExpanded={null}
        isSelected={_isNoneSelected}
        isSelectedDirectly={_isNoneSelected}
        label={<strong>None</strong>}
        ariaLabel="None"
        onClick={selectNone}
        onRequestToggleExpand={noop}
      />
    );
  }
  // Show a "select all" item if we allow multiple selections and the selections are not being filtered
  // by a query.
  if (props.multiple && !props.hasQuery && props.includeSelectAllItem) {
    selectAllItem = (
      <SelectTreeListItem
        disabled={props.disabled}
        icon={ModelTypeConstants.SOURCE}
        isExpanded={null}
        isSelected={areAllSelectableItemsSelected}
        isSelectedDirectly={areAllSelectableItemsSelected}
        columnWidths={props.columnWidths}
        label={<strong>{props.selectAllText ?? 'All'}</strong>}
        ariaLabel={props.selectAllText ?? 'All'}
        onClick={toggleSelectAll}
        onRequestToggleExpand={noop}
      />
    );
  }

  const loadMoreRootItems = props.onLoadMoreRootItems ?? noop;
  // Load another page of top-level results. Expanded items load their child pages in SelectTreeNode.
  let topLevelPageLoader: JSX.Element | null = null;
  if (props.isLoadingRootItems) {
    topLevelPageLoader = <SelectTreeLoadingNode disabled={false} text={`Loading ${props.itemTypeName}...`} />;
  } else if (props.moreRootItems) {
    if (props.autoLoadMore) {
      topLevelPageLoader = (
        <SelectTreeAutoLoadMoreNode
          disabled={props.disabled}
          itemTypeName={props.itemTypeName}
          onLoadMore={loadMoreRootItems}
        />
      );
    } else {
      topLevelPageLoader = (
        <SelectTreeLoadMoreNode disabled={props.disabled} onLoadMore={loadMoreRootItems} />
      );
    }
  }

  return (
    <ul
      className={classNames('selectTree', props.className, {
        'selectTree--disabled': props.disabled,
      })}
      ref={props.rootRef}
      {...(!props.submitHandler || props.disabled
        ? {}
        : {
            tabIndex: -1,
            onKeyPress: handleEnterPress,
          })}
      {...getTestProps({component: 'ControlledSelectTree', element: 'root'})}
    >
      {props.headers && (
        <SelectTreeListItem
          onRequestToggleExpand={noop}
          onClick={noop}
          columnWidths={props.columnWidths}
          ariaLabel={props.headers[0]}
          label={<strong className={css(styles.primaryHeader)}>{props.headers[0]}</strong>}
          labels={props.headers.slice(1).map((header) => (
            <strong key={header} className={css(styles.secondaryHeader)}>
              {header}
            </strong>
          ))}
          disabled={true}
        />
      )}
      {selectNoneItem}
      {props.rootItems.length > 0 && selectAllItem}
      {props.rootItems.map((node) => (
        <SelectTreeNode
          key={node.id}
          disabled={props.disabled || node.disabled}
          childrenDisabled={props.disabled}
          loadMoreFromIds={props.canLoadMore() ? props.loadMoreFromIds : null}
          autoLoadMore={props.autoLoadMore}
          item={node}
          onClickItem={props.handleClickedItemsChange}
          toggleExpanded={props.toggleExpanded}
          itemStateMap={props.itemStateMap}
          itemTypeName={props.itemTypeName}
          selectedIds={props.selectedIds}
          onChangeSelectedIds={props.onChangeSelectedIds}
          multiple={props.multiple}
          getNextSelectedIds={getNextSelectedIds}
          isLoadedItemExpandable={props.isLoadedItemExpandable}
          siblingSortFn={props.siblingSortFn}
          requiredRootIds={props.requiredRootIds}
          isItemSelectable={props.isItemSelectable}
          level={0}
          columnWidths={props.columnWidths}
          className={props.itemClassName}
          selectedClassName={props.selectedItemClassName}
          onHoverItem={props.onHoverItem}
        />
      ))}
      {topLevelPageLoader}
      {props.rootItems.length === 0 &&
        props.onLoadMoreRootItems &&
        !props.isLoadingRootItems &&
        !props.moreRootItems && <SelectTreeNotFoundNode disabled={false} text="No items found." />}
    </ul>
  );
}

export const styles = StyleSheet.create({
  primaryHeader: {
    color: NEAR_BLACK,
  },
  secondaryHeader: {
    color: GRAY_DARKER,
  },
});
